/**
 * Primitive color tokens — the raw brand palette, mirrored 1:1 from Figma Variables
 * (collection "colours" → Base). These are the ONLY place raw hex values live.
 *
 * Naming mirrors Figma: `<hue>` is the reference tone; the numeric suffix is the tint/shade
 * step from the brand ramp (e.g. 002/008/016/040 = light tints, 030/070/080 = greyscale value,
 * 116 = one step darker than the reference, 120 = text shade that reaches 4.5:1 on white, the page
 * surfaces and its own 008 tint — only where the reference tone cannot carry text). Semantics are assigned in the alias layer
 * (palette.ts) — do not give primitives meaning here.
 *
 * Source of truth: Figma. Update by re-importing the export, not by editing values ad hoc.
 */
export const base = {
  black: '#000000',
  white: '#ffffff',

  // Purple — brand primary
  purple: '#5e19ff',
  purple002: '#fcfaff',
  purple008: '#f2edff',
  purple016: '#e5daff',
  purple040: '#bfa3ff',

  // Midnight Blue — brand secondary
  midnightBlue: '#0c0b5a',
  midnightBlue002: '#fafafc',
  midnightBlue008: '#ecebf2',
  midnightBlue016: '#d8d8e5',
  midnightBlue040: '#9e9dbd',

  // Grey — neutrals (value scale: 002 lightest → 080 darkest)
  grey002: '#fbfafa',
  grey004: '#f8f7f6',
  grey006: '#f3f1f0',
  grey010: '#e9e7e6',
  grey016: '#dbd9d8',
  grey030: '#a5a5a5',
  grey070: '#5d6063',
  grey080: '#1a1b1c',

  // Yellow — warning
  yellow: '#f0b824',
  yellow002: '#fffefb',
  yellow008: '#fef9ed',
  yellow016: '#fdf4dc',
  yellow040: '#f9e3a7',
  yellow116: '#ca9b1e',
  yellow120: '#9d5c04',

  // Red — error
  red: '#ed324e',
  red002: '#fffbfb',
  red008: '#feeff1',
  red016: '#fcdee3',
  red040: '#f8adb8',
  red116: '#c72a42',
  red120: '#d41437',

  // Green — success
  green: '#00b866',
  green002: '#fafefc',
  green008: '#ebf9f3',
  green016: '#d6f4e7',
  green040: '#99d7bb',
  green116: '#009b56',
  green120: '#047c3d',

  // Denim — info
  denim: '#005d98',
  denim002: '#fafcfd',
  denim008: '#ebf2f7',
  denim016: '#d6e5ef',
  denim040: '#99bed6',
  denim116: '#004e80',

  // Brandbook accents — not yet assigned to semantics (reserved for charts/illustration)
  berry: '#560e5f',
  pink: '#d64287',
  turquoise: '#4eade0',
} as const;

export type Primitives = typeof base;
